import { Check, X } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Logo } from "@/components/Logo";

const comparisons = [
  {
    feature: "AI Seller Summaries",
    north: "Clear, human, seller-ready explanations",
    others: "Data shown without meaning"
  },
  {
    feature: "Agent Insights Engine",
    north: "Agent inputs + AI = strategic guidance",
    others: "No strategic interpretation"
  },
  {
    feature: "Market Intelligence",
    north: "ZIP-level demand, pricing, and trend analysis",
    others: "Basic MLS stats only"
  },
  {
    feature: "Neighborhood Insights",
    north: "Auto-generated, optimized local content",
    others: "Not offered"
  },
  {
    feature: "Narrative-Driven Reports",
    north: "Storytelling sellers understand",
    others: "Chart-heavy, agent-translated"
  },
  {
    feature: "Customization",
    north: "Personalized messaging per listing",
    others: "One-size-fits-all output"
  },
  {
    feature: "AI Context Layer",
    north: "Explains the \"why\" behind performance",
    others: "Leaves interpretation to the agent"
  },
  {
    feature: "API Integration",
    north: "Direct API access for custom workflows & automation",
    others: "No API or limited integrations"
  }
];

export function ComparisonTable() {
  return (
    <section className="container mx-auto px-6 py-12">
      <div className="max-w-6xl mx-auto">

        {/* Desktop & Tablet View (md and up) */}
        <Card className="hidden md:block overflow-hidden border-0 shadow-xl bg-gradient-to-br from-background via-background to-muted/20">
          <CardContent className="p-0">
            {/* Header Row */}
            <div className="grid grid-cols-[1fr_2fr_2fr] bg-gradient-to-r from-primary/5 via-primary/10 to-primary/5 border-b-2 border-primary/20">
              <div className="p-4 lg:p-6"></div>
              <div className="p-4 lg:p-6 flex items-center justify-center border-l-2 border-primary/20 bg-primary/5">
                <Logo size="sm" />
              </div>
              <div className="p-4 lg:p-6 text-center border-l-2 border-border/50">
                <h3 className="text-xl lg:text-2xl font-semibold text-muted-foreground/70">Others</h3>
              </div>
            </div>

            {/* Comparison Rows */}
            {comparisons.map((comparison, index) => (
              <div 
                key={index}
                className={`grid grid-cols-[1fr_2fr_2fr] ${
                  index % 2 === 0 ? 'bg-card' : 'bg-muted/10'
                } border-b border-border/50 last:border-b-0 hover:bg-primary/5 transition-all duration-200`}
              >
                <div className="p-4 lg:p-6 font-bold text-foreground flex items-center text-sm lg:text-base">
                  {comparison.feature}
                </div>
                <div className="p-4 lg:p-6 border-l-2 border-primary/20 bg-primary/5 flex items-start gap-2 lg:gap-3">
                  <div className="flex-shrink-0 mt-0.5">
                    <div className="h-5 w-5 lg:h-6 lg:w-6 rounded-full bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center shadow-lg">
                      <Check className="h-3 w-3 lg:h-4 lg:w-4 text-white font-bold" strokeWidth={3} />
                    </div>
                  </div>
                  <span className="text-foreground font-medium text-sm lg:text-base">{comparison.north}</span>
                </div>
                <div className="p-4 lg:p-6 border-l-2 border-border/50 flex items-start gap-2 lg:gap-3">
                  <div className="flex-shrink-0 mt-0.5">
                    <div className="h-5 w-5 lg:h-6 lg:w-6 rounded-full bg-muted flex items-center justify-center">
                      <X className="h-3 w-3 lg:h-4 lg:w-4 text-muted-foreground/50" strokeWidth={2} />
                    </div>
                  </div>
                  <span className="text-muted-foreground/70 text-sm lg:text-base">{comparison.others}</span>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Mobile View (below md) */}
        <div className="md:hidden space-y-4">
          {comparisons.map((comparison, index) => (
            <Card 
              key={index}
              className="overflow-hidden border-0 shadow-lg bg-gradient-to-br from-background via-background to-muted/20"
            >
              <CardContent className="p-0">
                <div className="bg-gradient-to-r from-primary/5 via-primary/10 to-primary/5 p-4 border-b-2 border-primary/20">
                  <h3 className="font-bold text-foreground text-base">{comparison.feature}</h3>
                </div>
                
                {/* NORTH Section */}
                <div className="p-4 bg-primary/5 border-b border-border/50">
                  <div className="flex items-center justify-center mb-3">
                    <Logo size="sm" />
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 mt-0.5">
                      <div className="h-6 w-6 rounded-full bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center shadow-lg">
                        <Check className="h-4 w-4 text-white font-bold" strokeWidth={3} />
                      </div>
                    </div>
                    <span className="text-foreground font-medium text-sm">{comparison.north}</span>
                  </div>
                </div>
                
                {/* Others Section */}
                <div className="p-4 bg-card">
                  <div className="text-center mb-3">
                    <h4 className="text-lg font-semibold text-muted-foreground/70">Others</h4>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 mt-0.5">
                      <div className="h-6 w-6 rounded-full bg-muted flex items-center justify-center">
                        <X className="h-4 w-4 text-muted-foreground/50" strokeWidth={2} />
                      </div>
                    </div>
                    <span className="text-muted-foreground/70 text-sm">{comparison.others}</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        <div className="mt-8 text-center">
          <p className="text-sm text-muted-foreground">
            Join other agents who choose NORTH for their sellers
          </p>
        </div>
      </div>
    </section>
  );
}
